import React from "react";
import { FaPhone, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";
import { useInView } from "react-intersection-observer";
import { useTranslation } from 'react-i18next';
import SEO from "../components/SEO";

// Contact information from environment variables
const PHONE_NUMBER = "+385 91 433 0252";
const EMAIL_ADDRESS = "marko.dula@axialis-motors.com";
const OFFICE_ADDRESS_LINE1 = "Ivana Pavla II, 12";
const OFFICE_ADDRESS_LINE2 = "Otok dalmatinski";

const Contact = () => {
  const { t } = useTranslation();

  // Colors for consistent theming
  const primaryTextColor = "text-[#212529]"; // Dark text
  const secondaryTextColor = "text-[#212529]/80"; // Medium gray
  const tertiaryTextColor = "text-[#212529]/60"; // Light gray

  const [headerRef, headerInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [infoRef, infoInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <div className="min-h-screen font-sans overflow-hidden relative">
      <SEO 
        title="Contact Axialis - Get in Touch"
        description="Contact Axialis for inquiries about our yokeless axial flux electric motors. Phone, email, and office location details."
        keywords="contact Axialis, axial flux motor contact, electric motor inquiries, Axialis location, Croatia electric motors"
        path="/contact"
      />
      {/* Hero Section */}
      <section
        ref={headerRef}
        className="relative pt-32 pb-8">
        <div className="container mx-auto px-4 relative z-10">
          <div className={`text-center transition-all duration-1000 transform ${headerInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
            <h1 className={`text-5xl md:text-6xl font-serif font-medium mb-6 ${primaryTextColor}`}>
              Contact
            </h1>
            <div className="h-0.5 w-16 bg-primary mx-auto mb-6"></div>
            <p className={`text-lg md:text-xl ${secondaryTextColor} leading-relaxed mb-10`}>
              Get in touch with our team for inquiries about our axial flux motor technology
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information Cards Section */}
      <section
        ref={infoRef}
        className="py-6 relative"
      >
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Phone Card */}
            <div
              className={`backdrop-blur-sm bg-white/40 rounded-xl p-6 shadow-xl border border-white/40 transition-all duration-1000 transform ${infoInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
              style={{ transitionDelay: '0ms' }}
            >
              <div className="flex flex-col items-center text-center">
                <div className="bg-white/60 p-4 rounded-full mb-5 shadow-md border border-white/40 transform transition-all duration-300 hover:scale-105">
                  <FaPhone className="text-primary text-2xl" />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-[#212529]">Phone</h3>
                <p className="text-[#212529]/70 mb-3">Call us directly</p>
                <a
                  href={`tel:${PHONE_NUMBER}`}
                  className="text-[#212529] font-medium text-lg hover:text-primary transition-colors duration-300"
                >
                  {PHONE_NUMBER}
                </a>
              </div>
            </div>

            {/* Email Card */}
            <div
              className={`backdrop-blur-sm bg-white/40 rounded-xl p-6 shadow-xl border border-white/40 transition-all duration-1000 transform ${infoInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
              style={{ transitionDelay: '200ms' }}
            >
              <div className="flex flex-col items-center text-center">
                <div className="bg-white/60 p-4 rounded-full mb-5 shadow-md border border-white/40 transform transition-all duration-300 hover:scale-105">
                  <FaEnvelope className="text-primary text-2xl" />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-[#212529]">Email</h3>
                <p className="text-[#212529]/70 mb-3">Send us an email</p>
                <a
                  href={`mailto:${EMAIL_ADDRESS}`}
                  className="text-[#212529] font-medium text-lg hover:text-primary transition-colors duration-300 break-all"
                >
                  {EMAIL_ADDRESS}
                </a>
              </div>
            </div>

            {/* Location Card */}
            <div
              className={`backdrop-blur-sm bg-white/40 rounded-xl p-6 shadow-xl border border-white/40 transition-all duration-1000 transform ${infoInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
              style={{ transitionDelay: '400ms' }}
            >
              <div className="flex flex-col items-center text-center">
                <div className="bg-white/60 p-4 rounded-full mb-5 shadow-md border border-white/40 transform transition-all duration-300 hover:scale-105">
                  <FaMapMarkerAlt className="text-primary text-2xl" />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-[#212529]">Visit Us</h3>
                <p className="text-[#212529]/70 mb-3">Our office location</p>
                <p className="text-[#212529]">{OFFICE_ADDRESS_LINE1}</p>
                <p className="text-[#212529]">{OFFICE_ADDRESS_LINE2}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12 relative">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto overflow-hidden rounded-xl shadow-xl border border-white/40">
            <div className="relative">
              <iframe
                className="w-full h-[300px] md:h-[400px]"
                src="https://www.google.com/maps?q=Ivana+Pavla+II+12,+Otok+dalmatinski,+Croatia&hl=en&z=15&output=embed"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Office Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;